import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClientProvider, QueryClient } from '@tanstack/react-query'
import { AuthProvider } from './context/AuthContext'
import { ThemeProvider } from './context/ThemeContext'
import Login from './pages/Login'
import Register from './pages/Register.tsx'
import VerifyEmail from './pages/VerifyEmail'
import ForgotPassword from './pages/ForgotPassword.tsx'
import ResetPassword from './pages/ResetPassword.tsx'
import ProtectedRoute from './components/auth/ProtectedRoute'
import AccessRoute, { OptimizationEditorRoute } from './components/auth/AccessRoute'
import TeamSettings from './pages/TeamSettings'
import AcceptInvitation from './pages/AcceptInvitation'
import AccountSecurity from './pages/AccountSecurity'
import BillingAccess from './pages/BillingAccess'
import AppLayout from './components/layout/AppLayout'
import Dashboard from './pages/Dashboard'
import OptimizationList from './pages/Optimization/OptimizationList'
import OptimizationPage from './pages/Optimization/OptimizationPage'
import OptimizationReplan from './pages/Optimization/OptimizationReplan'
import OptimizationViewer from './pages/Optimization/OptimizationViewer'
import ProjectHistory from './pages/Optimization/ProjectHistory'
import Settings from './pages/Settings'
import ProductSettings from './pages/ProductSettings'
import VehicleSettings from './pages/VehicleSettings'
import ADRSettings from './pages/ADRSettings.tsx'
import SharedOptimizationPlan from './pages/Optimization/SharedOptimizationPlan'
import SubscriptionPage from './pages/Subscription'
import Checkout from './pages/Checkout'
import Welcome from './pages/Welcome'
import { LocaleProvider } from './context/LocaleContext'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <LocaleProvider><AuthProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invitation" element={<AcceptInvitation />} />
            <Route path="/shared/:token" element={<SharedOptimizationPlan />} />
            <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />

            {/* Protected routes */}
            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="optimization" element={<AccessRoute resource="optimization"><OptimizationList /></AccessRoute>} />
              <Route path="optimization/new" element={<OptimizationEditorRoute><OptimizationPage /></OptimizationEditorRoute>} />
              <Route path="optimization/:runId" element={<OptimizationEditorRoute><OptimizationPage /></OptimizationEditorRoute>} />
              <Route path="optimization/:runId/replan" element={<AccessRoute resource="optimization" action="write"><OptimizationReplan /></AccessRoute>} />
              <Route path="optimization/:runId/viewer" element={<AccessRoute resource="optimization"><OptimizationViewer /></AccessRoute>} />
              <Route path="optimization/projects/:jobId" element={<AccessRoute resource="optimization"><ProjectHistory /></AccessRoute>} />
              <Route path="settings" element={<AccessRoute resource="optimization_settings"><Settings /></AccessRoute>} />
              <Route path="team" element={<AccessRoute resource="user"><TeamSettings /></AccessRoute>} />
              <Route path="account/security" element={<AccountSecurity />} />
              <Route path="subscription" element={<SubscriptionPage />} />
              <Route path="subscription/access" element={<BillingAccess />} />
              <Route path="welcome" element={<Welcome />} />
              <Route path="products" element={<AccessRoute resource="product"><ProductSettings /></AccessRoute>} />
              <Route path="vehicles" element={<AccessRoute resource="vehicle"><VehicleSettings /></AccessRoute>} />
              <Route path="adr-settings" element={<AccessRoute resource="reference_data"><ADRSettings /></AccessRoute>} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </AuthProvider></LocaleProvider>
      </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export default App
